import { Tool } from '@procureai/agents';
import { DynamicToolUIPart, ToolUIPart, UITool } from 'ai';

import { AskGlobalExternalSourcerTool } from './AskGlobalExternalSourcerTool';
import { AskInternalSourcerTool } from './AskInternalSourcerTool';
import { AskWebSearchTool } from './AskWebSearchTool';
import { CatalogueSearchTool } from './CatalogueSearchTool';
import { ComplianceCheckWorkflow } from './ComplianceCheckWorkflow';
import { ContractComparisonTool } from './ContractComparisonTool';
import { ContractItemTool } from './ContractItemTool';
import { ExtendedLineItemsCheckTool } from './ExtendedLineItemsCheckTool';
import { ExtractQuoteDetails } from './ExtractQuoteDetails';
import { ExtractSupplierRequestTool } from './ExtractSupplierRequestTool';
import { HandoverToRouteTool } from './HandoverToRouteTool';
import { IdentifyCommodityGroupTool } from './IdentifyCommodityGroupTool';
import { ProcurementKnowledgeTool } from './ProcurementKnowledgeTool';
import { PurchaseOrderItemTool } from './PurchaseOrderItemTool';
import { PurchaseOrderTool } from './PurchaseOrderTool';
import { PurchaseRequisitionItemTool } from './PurchaseRequisitionItemTool';
import { QuoteComparisonTool } from './QuoteComparisonTool';
import { ReadDemandInformationTool } from './ReadDemandInformationTool';
import { ReadIntakeRequest } from './ReadIntakeRequest';
import { RunCheckQuoteRisk } from './RunCheckQuoteRisk';
import { RunCheckQuoteRiskFromDemandTool } from './RunCheckQuoteRiskFromDemandTool';
import { RunContractItemDiscovery } from './RunContractItemDiscovery';
import { RunReadAttachments } from './RunReadAttachments';
import { RunSourceSuppliers } from './RunSourceSuppliers';
import { RunSupplierDiscovery } from './RunSupplierDiscovery';
import { RunUpsertIntakeRequest } from './RunUpsertIntakeRequest';
import { SearchCommodityGroupTool } from './SearchCommodityGroupTool';
import { SearchMaterialTool } from './SearchMaterialTool';
import { SupplierStructuredSearchTool } from './SupplierStructuredSearchTool';
import { SupplierText2CypherTool } from './SupplierText2CypherTool';
import { SupplierTool } from './SupplierTool';
import { VetSuppliersTool } from './VetSuppliersTool';

export enum DevelopmentTool {
  workflowRequesterCompliance = 'runRequesterCompliance',
  runUpsertIntakeRequest = 'runUpsertIntakeRequest',
  readIntakeRequest = 'readIntakeRequest',
  searchPurchaseOrder = 'searchPurchaseOrder',
  runContractItemDiscovery = 'runContractItemDiscovery',
}

// The map now accepts string keys, but we only populate it with known tool names.
export const ToolComponentMap: {
  [key in Tool | DevelopmentTool]?: ToolResultComponent<any, any, any>;
} = {
  [Tool.searchCatalogue]: CatalogueSearchTool,
  [Tool.searchProcurementKnowledge]: ProcurementKnowledgeTool,
  [Tool.searchContractItem]: ContractItemTool,
  [Tool.searchPurchaseOrderItem]: PurchaseOrderItemTool,
  [Tool.searchPurchaseRequisitionItem]: PurchaseRequisitionItemTool,
  [Tool.searchSupplier]: SupplierTool,
  [Tool.handoverToRoute]: HandoverToRouteTool,
  [Tool.identifyCommodityGroup]: IdentifyCommodityGroupTool,
  [Tool.supplierStructuredSearch]: SupplierStructuredSearchTool,
  [Tool.supplierText2Cypher]: SupplierText2CypherTool,
  [Tool.quoteComparator]: QuoteComparisonTool,
  [Tool.contractComparator]: ContractComparisonTool,
  [Tool.askInternalSourcer]: AskInternalSourcerTool,
  [Tool.askExternalSourcer]: AskGlobalExternalSourcerTool,
  [Tool.askWebSearch]: AskWebSearchTool,
  [Tool.vetSuppliers]: VetSuppliersTool,
  [Tool.extractSupplierRequest]: ExtractSupplierRequestTool,
  [Tool.readDemandInformation]: ReadDemandInformationTool,
  [Tool.extendedLineItemsCheck]: ExtendedLineItemsCheckTool,
  [Tool.runReadAttachments]: RunReadAttachments,
  [Tool.runCheckQuoteRisk]: RunCheckQuoteRisk,
  [Tool.runCheckQuoteRiskFromDemand]: RunCheckQuoteRiskFromDemandTool,
  [Tool.extractQuoteDetails]: ExtractQuoteDetails,
  [Tool.runSupplierDiscovery]: RunSupplierDiscovery,
  [Tool.runSourceSuppliers]: RunSourceSuppliers,
  [Tool.runContractItemDiscovery]: RunContractItemDiscovery,
  [Tool.searchMaterial]: SearchMaterialTool,
  [Tool.searchCommodityGroup]: SearchCommodityGroupTool,

  // in development tools / workflows not inculded in '@procureai/agents' yet
  [DevelopmentTool.workflowRequesterCompliance]: ComplianceCheckWorkflow,
  [DevelopmentTool.runUpsertIntakeRequest]: RunUpsertIntakeRequest,
  [DevelopmentTool.readIntakeRequest]: ReadIntakeRequest,
  [DevelopmentTool.searchPurchaseOrder]: PurchaseOrderTool,
};

export type ToolNames = keyof typeof ToolComponentMap;
export type TOOLS = Record<ToolNames, UITool>;

type TypedToolPart<NAME extends ToolNames, ARGS, RESULT> =
  Extract<ToolUIPart<TOOLS> | DynamicToolUIPart, { type: `tool-${NAME}` } | { type: 'dynamic-tool' }> extends infer T
    ? T extends { state: infer STATE }
      ? Omit<T, 'input' | 'output'> & { input: ARGS } & (STATE extends 'output-available'
            ? { output: RESULT }
            : { output?: undefined })
      : never
    : never;

export type ToolComponentProps<NAME extends ToolNames, ARGS, RESULT> = {
  tool: TypedToolPart<NAME, ARGS, RESULT>;
  // Forwarding of the tool name to avoid manual parsing or restating known information through generic typing
  toolName: NAME;
};

export type ToolResultComponent<NAME extends ToolNames, ARGS = unknown, RESULT = unknown> = React.FC<
  ToolComponentProps<NAME, ARGS, RESULT>
>;
